import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../context/SearchContext';
import { useUserType, USER_TYPES } from '../../context/UserTypeContext';
import { getSearchResults, getBuyerCountries } from '../../services/SearchService';
import { categoryService } from '../../api/categoryService';
import './SearchBar.scss';

export const SearchBar = ({ onResultsUpdate = () => {} }) => {
  const { userType, isBrand, isUserTypeReady } = useUserType();
  const { searchQuery, setSearchQuery } = useSearch();
  const navigate = useNavigate();
  
  // State management
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]); 
  const [searchError, setSearchError] = useState(null);
  const [useFuzzySearch, setUseFuzzySearch] = useState(true); // Default to true for better user experience
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        // For brands, we might want a different set of categories than for buyers
        const params = userType === USER_TYPES.BRAND 
          ? { withProducts: true } // Only show categories with products for brands
          : {};
          
        const categoriesData = await categoryService.getAllCategories(params);
        console.log("Fetched categories:", categoriesData);
        
        if (Array.isArray(categoriesData.results)) {
          setCategories(categoriesData.results);
        } else if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        } else {
          console.error("Unexpected categories data format:", categoriesData);
          setCategories([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    if (isUserTypeReady) {
      fetchCategories();
    }
  }, [userType, isUserTypeReady]);

  // Fetch locations based on user type
  useEffect(() => {
    if (isUserTypeReady && isBrand) {
      getBuyerCountries().then(countries => setAvailableLocations(countries || []));
    }
  }, [isBrand, isUserTypeReady]);

  // Handle clicks outside to collapse the search bar
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchBar = document.querySelector('.search-bar');
      if (searchBar && !searchBar.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle clicks outside to close the category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.search-bar__category-dropdown');
      if (categoryDropdownOpen && dropdown && !dropdown.contains(event.target) && 
          !event.target.closest('.search-bar__category-selector')) {
        setCategoryDropdownOpen(false);
      }
    };

    if (categoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdownOpen]);
  
  // Get placeholder text based on user type
  const getSearchPlaceholder = useCallback(() => {
    const basePlaceholder = userType === USER_TYPES.BRAND ? 'Buyers' : 'Products';
    return isExpanded ? `Search for ${basePlaceholder.toLowerCase()}...` : basePlaceholder;
  }, [userType, isExpanded]);

  // Show all products/buyers function
  const handleShowAll = useCallback(() => {
    if (userType === USER_TYPES.BRAND) {
      // For brand users, show all buyers in the lead-list
      navigate('/lead-list', { 
        state: { 
          showAll: true,
          searchType: 'buyer',
          filters: {},
          fuzzyEnabled: false
        } 
      });
    } else {
      // For buyers and unregistered users, show all products
      navigate('/products', { 
        state: { 
          showAll: true,
          searchType: 'product',
          filters: {},
          fuzzyEnabled: false
        } 
      });
    }
    // Collapse the search bar after navigating
    setIsExpanded(false);
  }, [userType, navigate]);

  // Perform the actual search
  const performSearch = useCallback(async () => {
    // Allow searching with just category selection, even without query text
    if ((!searchQuery.trim() && !selectedCategory) || !isUserTypeReady) return;
    
    setIsSearching(true);
    setSearchError(null);
    
    try {
      // Build filters
      const filters = {};
      
      // Handle category selection properly
      if (selectedCategory && selectedCategory !== 'all') {
        filters.category = selectedCategory; // This will be a category ID now
      }
      
      if (selectedLocation) filters.location = selectedLocation;
      
      // Map user type to search type
      const searchType = userType === USER_TYPES.BRAND ? 'brand' : 
                        userType === USER_TYPES.BUYER ? 'buyer' : 'unregistered';
      
      console.log(`Performing search with type: ${searchType}, query: "${searchQuery}", filters:`, filters);
      
      // Instead of showing results in a popup, navigate to the appropriate page
      if (userType === USER_TYPES.BRAND) {
        // Brands search for buyers, navigate to lead-list
        navigate('/lead-list', { 
          state: { 
            searchQuery, 
            searchType: 'buyer',
            filters,
            fuzzyEnabled: useFuzzySearch
          } 
        });
      } else {
        // Buyers and unregistered users search for products
        navigate('/products', { 
          state: { 
            searchQuery,
            searchType: 'product',
            filters,
            fuzzyEnabled: useFuzzySearch
          } 
        });
      }
      
      // Collapse the search bar after navigating
      setIsExpanded(false);
      
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error.message || 'Error performing search');
      
      // Call the onResultsUpdate with error info for backward compatibility
      onResultsUpdate({ 
        loading: false, 
        results: [], 
        error, 
        type: userType,
        query: searchQuery
      });
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, selectedCategory, selectedLocation, userType, isUserTypeReady, useFuzzySearch, navigate, onResultsUpdate]);

  // Event handlers
  const handleSearch = () => performSearch();
  const handleKeyPress = (e) => e.key === 'Enter' && handleSearch();
  const handleClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };
  const toggleFuzzySearch = () => setUseFuzzySearch(prev => !prev);
  
  // Toggle category dropdown
  const toggleCategoryDropdown = (e) => {
    e.stopPropagation();
    setCategoryDropdownOpen(!categoryDropdownOpen);
  };
  
  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setCategoryDropdownOpen(false);
  };
  
  // Get selected category name for display
  const getSelectedCategoryName = () => {
    if (!selectedCategory) {
      return userType === USER_TYPES.BRAND ? 'What category of buyer?' : 'Select category';
    }
    
    if (selectedCategory === 'all') {
      return userType === USER_TYPES.BRAND ? 'All Buyers' : 'All Products';
    }
    
    const found = categories.find(cat => cat.id.toString() === selectedCategory.toString());
    return found ? found.name : 'Selected Category';
  };

  // Render component parts
  const renderCategorySection = () => {
    return (
      <div className="search-bar__section">
        <span 
          className="search-bar__label search-bar__label--clickable" 
          onClick={toggleCategoryDropdown}
        >
          {userType === USER_TYPES.BRAND ? 'Buyer category' : 'Alcohol category'}
        </span>
        
        <div className="search-bar__category-wrapper">
          <div 
            className="search-bar__category-selector" 
            onClick={toggleCategoryDropdown}
          >
            <span className={`search-bar__selected-category ${!selectedCategory ? 'placeholder' : ''}`}>
              {loadingCategories ? 'Loading categories...' : getSelectedCategoryName()}
            </span>
            <svg 
              className={`search-bar__dropdown-arrow ${categoryDropdownOpen ? 'open' : ''}`} 
              width="14" 
              height="8" 
              viewBox="0 0 14 8" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 1L7 7L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          {categoryDropdownOpen && (
            <div className="search-bar__category-dropdown">
              <div className="search-bar__category-item" onClick={() => handleCategorySelect('')}>
                <span>{userType === USER_TYPES.BRAND ? 'What category of buyer?' : 'Select category'}</span>
              </div>
              <div className="search-bar__category-item" onClick={() => handleCategorySelect('all')}>
                <span>{userType === USER_TYPES.BRAND ? 'All Buyers' : 'All Products'}</span>
              </div>
              
              <div className="search-bar__category-grid">
                {categories.map(category => (
                  <div 
                    key={category.id} 
                    className={`search-bar__category-item ${selectedCategory === category.id.toString() ? 'selected' : ''}`}
                    onClick={() => handleCategorySelect(category.id.toString())}
                  >
                    <span>{category.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSearchInput = () => {
    return (
      <div className="search-bar__input-wrapper">
        <svg 
          className="search-bar__input-icon" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        
        {isExpanded ? (
          <input
            type="text"
            className="search-bar__input"
            placeholder={getSearchPlaceholder()}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
          />
        ) : (
          <div className="search-bar__placeholder" onClick={() => setIsExpanded(true)}>
            {getSearchPlaceholder()}
          </div>
        )}
        
        <button
          className={`search-bar__button ${isSearching ? 'searching' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleSearch();
          }}
          disabled={isSearching}
        >
          {isSearching ? (
            <span>Searching...</span>
          ) : (
            <>
              <svg 
                className="search-bar__button-icon" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>Search</span>
            </>
          )}
        </button>
      </div>
    );
  };

  const renderLocationSection = () => {
    const countries = userType === USER_TYPES.BRAND 
      ? availableLocations.map(country => (
          <option key={country} value={country}>{country}</option>
        ))
      : (
        <>
          <option value="United States">United States</option>
          <option value="United Kingdom">United Kingdom</option>
          <option value="France">France</option>
          <option value="Germany">Germany</option>
          <option value="Italy">Italy</option>
          <option value="Spain">Spain</option>
          <option value="Netherlands">Netherlands</option>
        </>
      );
    
    return (
      <div className="search-bar__section">
        <span className="search-bar__label">
          {userType === USER_TYPES.BRAND ? 'Origin' : 'Location'}
        </span>
        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          className="search-bar__select"
        >
          <option value="">From where?</option>
          {countries}
        </select>
      </div>
    );
  };

  const renderShowAllButton = () => {
    return (
      <button
        className="search-bar__show-all-button"
        onClick={handleShowAll}
      >
        Show All
      </button>
    );
  };

  return (
    <div 
      className={`search-bar ${isExpanded ? 'search-bar--expanded' : ''}`}
      onClick={handleClick}
    >
      <div className="search-bar__input-group">
        {renderCategorySection()}
        <div className="search-bar__divider" />
        {renderSearchInput()}
        <div className="search-bar__divider" />
        {renderLocationSection()}
      </div>
      
      {isExpanded && (
        <div className="search-bar__options">
          <label className="search-bar__option">
            <input
              type="checkbox"
              checked={useFuzzySearch}
              onChange={toggleFuzzySearch}
              className="search-bar__checkbox"
            />
            <span className="search-bar__option-label">Smart Search (finds similar matches)</span>
          </label>
        </div>
      )}
      
      {isExpanded && (
        <div className="search-bar__show-all">
          {renderShowAllButton()}
        </div>
      )}
      
      {searchError && (
        <div className="search-bar__error-message">
          Error: {searchError}
        </div>
      )}
    </div>
  );
};